import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";

import { apiClient } from "../../services/api/apiClient";
import { fmtHa, fmtKg } from "./adminDashboardShared";

const { Text } = Typography;

interface CompareSeason {
  id: number;
  label: string;
  year: number;
}

interface ProvinceRow {
  province_id: number | null;
  province_name: string;
  region_id: number | null;
  region_name: string;
  licensed_ha: number;
  audited_ha: number;
  productive_ha: number;
  declared_kg: number;
  audited_kg: number;
  planned_count: number;
  finalized_count: number;
  prev: {
    licensed_ha: number;
    audited_ha: number;
    productive_ha: number;
    declared_kg: number;
    audited_kg: number;
    planned_count: number;
    finalized_count: number;
  } | null;
}

interface PayloadT {
  role: "admin";
  scope: "province_summary";
  season: { id: number; label: string; year: number } | null;
  prev_season: { id: number; label: string; year: number } | null;
  available_compare_seasons: CompareSeason[];
  regions: Array<{ id: number; name: string }>;
  provinces: ProvinceRow[];
}

interface Props {
  season: number | null;
}

/** Devuelve ratio kg/ha_auditada (0 si no hay ha auditada). */
const kgPerHa = (kg: number, ha: number) => (ha > 0 ? kg / ha : 0);

/** Delta % relativo, con guardrails: null si no hay prev o prev=0. */
const deltaPct = (
  current: number | undefined,
  prev: number | undefined,
): number | null => {
  if (prev == null || prev === 0) return null;
  const c = current ?? 0;
  return ((c - prev) / prev) * 100;
};

/** Helper visual: valor principal + delta% vs prev debajo (si existe). */
const renderWithDelta = (
  current: number,
  prev: number | undefined,
  formatter: (v: number) => string,
) => {
  const pct = deltaPct(current, prev);
  return (
    <div style={{ lineHeight: 1.25 }}>
      <div>{formatter(current)}</div>
      {pct != null && (
        <small
          style={{
            color: pct > 0 ? "#52c41a" : pct < 0 ? "#f5222d" : "#8c8c8c",
            fontSize: 10,
          }}
        >
          {pct > 0 ? <ArrowUpOutlined /> : pct < 0 ? <ArrowDownOutlined /> : null}
          {" "}
          {pct > 0 ? "+" : ""}
          {pct.toFixed(1)}%
        </small>
      )}
    </div>
  );
};

export const AdminProvincePanel = ({ season }: Props) => {
  const [payload, setPayload] = useState<PayloadT | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Campaña con la que comparar. `null` = default backend (año-1
  // misma variedad+país). El usuario puede cambiarla con el Select
  // "Comparar vs" cuando el default no sea representativo (p.ej. una
  // campaña anterior infra-poblada en datos).
  const [compareWith, setCompareWith] = useState<number | null>(null);

  // Reset del compareWith cuando cambia la campaña principal — el
  // año-1 relativo cambia también.
  useEffect(() => {
    setCompareWith(null);
  }, [season]);

  useEffect(() => {
    if (season == null) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          scope: "province_summary",
          season: String(season),
        });
        if (compareWith != null) qs.set("compare_with", String(compareWith));
        const res = await apiClient.get<PayloadT>(
          `/v1/dashboard/?${qs.toString()}`,
        );
        setPayload(res.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "No se pudo cargar el resumen por provincia",
        );
        setPayload(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [season, compareWith]);

  const regionFilters = useMemo(
    () =>
      (payload?.regions ?? []).map((r) => ({
        text: r.name,
        value: String(r.id),
      })),
    [payload?.regions],
  );

  if (error) return <Alert type="error" message={error} showIcon />;

  return (
    <Spin spinning={loading} tip="Cargando resumen por provincia…">
      {payload && (
        <Card
          title={
            <Space
              style={{ width: "100%", justifyContent: "space-between" }}
              wrap
            >
              <span>Resumen por provincia</span>
              <Space size="small">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Comparar vs:
                </Text>
                <Select
                  size="small"
                  style={{ minWidth: 240 }}
                  value={compareWith ?? payload.prev_season?.id ?? undefined}
                  onChange={(v) => setCompareWith(v)}
                  options={payload.available_compare_seasons.map((s) => ({
                    label: s.label,
                    value: s.id,
                  }))}
                  placeholder={
                    payload.prev_season?.label ?? "Sin campaña previa"
                  }
                  disabled={payload.available_compare_seasons.length === 0}
                  allowClear
                  onClear={() => setCompareWith(null)}
                />
              </Space>
            </Space>
          }
        >
          <Table
            size="small"
            rowKey={(r) => String(r.province_id ?? "none")}
            dataSource={payload.provinces}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 1500 }}
            columns={[
              {
                title: "Provincia",
                dataIndex: "province_name",
                key: "province_name",
                fixed: "left",
                width: 160,
                sorter: (a, b) =>
                  a.province_name.localeCompare(b.province_name),
              },
              {
                title: "Región",
                dataIndex: "region_name",
                key: "region_name",
                width: 180,
                filters: regionFilters,
                filterSearch: true,
                onFilter: (value, record) =>
                  String(record.region_id ?? "") === value,
                sorter: (a, b) => a.region_name.localeCompare(b.region_name),
              },
              {
                title: "Ha licenciada",
                dataIndex: "licensed_ha",
                key: "licensed_ha",
                width: 130,
                align: "right",
                sorter: (a, b) => a.licensed_ha - b.licensed_ha,
                defaultSortOrder: "descend",
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.licensed_ha, fmtHa),
              },
              {
                title: "Ha auditada",
                dataIndex: "audited_ha",
                key: "audited_ha",
                width: 130,
                align: "right",
                sorter: (a, b) => a.audited_ha - b.audited_ha,
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.audited_ha, fmtHa),
              },
              {
                title: "Ha productiva",
                dataIndex: "productive_ha",
                key: "productive_ha",
                width: 140,
                align: "right",
                sorter: (a, b) => a.productive_ha - b.productive_ha,
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.productive_ha, fmtHa),
              },
              {
                title: "Kg declarados",
                dataIndex: "declared_kg",
                key: "declared_kg",
                width: 150,
                align: "right",
                sorter: (a, b) => a.declared_kg - b.declared_kg,
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.declared_kg, fmtKg),
              },
              {
                title: "Kg auditados",
                dataIndex: "audited_kg",
                key: "audited_kg",
                width: 150,
                align: "right",
                sorter: (a, b) => a.audited_kg - b.audited_kg,
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.audited_kg, fmtKg),
              },
              {
                title: "Kg / ha auditada",
                key: "kg_per_ha",
                width: 150,
                align: "right",
                sorter: (a, b) =>
                  kgPerHa(a.audited_kg, a.audited_ha) -
                  kgPerHa(b.audited_kg, b.audited_ha),
                render: (_: any, r) => {
                  const current = kgPerHa(r.audited_kg, r.audited_ha);
                  const prev = r.prev
                    ? kgPerHa(r.prev.audited_kg, r.prev.audited_ha)
                    : undefined;
                  if (current === 0) return "—";
                  return renderWithDelta(
                    current,
                    prev,
                    (v) =>
                      `${Math.round(v).toLocaleString("es-ES")} kg/ha`,
                  );
                },
              },
              {
                title: "Audits plan.",
                dataIndex: "planned_count",
                key: "planned_count",
                width: 110,
                align: "right",
                sorter: (a, b) => a.planned_count - b.planned_count,
                render: (v: number, r) =>
                  renderWithDelta(v, r.prev?.planned_count, (x) => String(x)),
              },
              {
                title: "Audits finalizadas",
                dataIndex: "finalized_count",
                key: "finalized_count",
                width: 140,
                align: "right",
                sorter: (a, b) => a.finalized_count - b.finalized_count,
                render: (v: number, r) =>
                  renderWithDelta(
                    v,
                    r.prev?.finalized_count,
                    (x) => String(x),
                  ),
              },
            ]}
            // Fila de totales al pie — suma los visibles tras filtro.
            // Al filtrar por región los totales se recalculan.
            summary={(rows) => {
              const t = (rows as ProvinceRow[]).reduce(
                (acc, r) => ({
                  licensed_ha: acc.licensed_ha + r.licensed_ha,
                  audited_ha: acc.audited_ha + r.audited_ha,
                  productive_ha: acc.productive_ha + r.productive_ha,
                  declared_kg: acc.declared_kg + r.declared_kg,
                  audited_kg: acc.audited_kg + r.audited_kg,
                  planned_count: acc.planned_count + r.planned_count,
                  finalized_count: acc.finalized_count + r.finalized_count,
                  prev: {
                    licensed_ha:
                      acc.prev.licensed_ha + (r.prev?.licensed_ha ?? 0),
                    audited_ha:
                      acc.prev.audited_ha + (r.prev?.audited_ha ?? 0),
                    productive_ha:
                      acc.prev.productive_ha + (r.prev?.productive_ha ?? 0),
                    declared_kg:
                      acc.prev.declared_kg + (r.prev?.declared_kg ?? 0),
                    audited_kg:
                      acc.prev.audited_kg + (r.prev?.audited_kg ?? 0),
                    planned_count:
                      acc.prev.planned_count +
                      (r.prev?.planned_count ?? 0),
                    finalized_count:
                      acc.prev.finalized_count +
                      (r.prev?.finalized_count ?? 0),
                  },
                }),
                {
                  licensed_ha: 0,
                  audited_ha: 0,
                  productive_ha: 0,
                  declared_kg: 0,
                  audited_kg: 0,
                  planned_count: 0,
                  finalized_count: 0,
                  prev: {
                    licensed_ha: 0,
                    audited_ha: 0,
                    productive_ha: 0,
                    declared_kg: 0,
                    audited_kg: 0,
                    planned_count: 0,
                    finalized_count: 0,
                  },
                },
              );
              const kgha = kgPerHa(t.audited_kg, t.audited_ha);
              const prevKgha = kgPerHa(
                t.prev.audited_kg,
                t.prev.audited_ha,
              );
              const bold = { fontWeight: 600 };
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <span style={bold}>Total</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>—</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.licensed_ha,
                          t.prev.licensed_ha || undefined,
                          fmtHa,
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.audited_ha,
                          t.prev.audited_ha || undefined,
                          fmtHa,
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.productive_ha,
                          t.prev.productive_ha || undefined,
                          fmtHa,
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.declared_kg,
                          t.prev.declared_kg || undefined,
                          fmtKg,
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.audited_kg,
                          t.prev.audited_kg || undefined,
                          fmtKg,
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      <span style={bold}>
                        {kgha === 0
                          ? "—"
                          : renderWithDelta(
                              kgha,
                              prevKgha || undefined,
                              (v) =>
                                `${Math.round(v).toLocaleString("es-ES")} kg/ha`,
                            )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.planned_count,
                          t.prev.planned_count || undefined,
                          (x) => String(x),
                        )}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      <span style={bold}>
                        {renderWithDelta(
                          t.finalized_count,
                          t.prev.finalized_count || undefined,
                          (x) => String(x),
                        )}
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}
    </Spin>
  );
};
